import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ETFsQuoteDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Quote - ETFs API" description="Query quote data via the ETFs API; request parameters, code examples and the response schema for the quote endpoint."  canonicalUrl="https://axionquant.com/docs/01-market-data/ETFs/04-quote" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Quote - ETFs API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get ETF Quote */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/:ticker/quote
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves a comprehensive quote for a specific ETF ticker. Returns the latest OHLC values, exponential moving averages (EMA30, EMA60, EMA90, EMA120), 52-week range statistics, year-to-date performance, and volume data for comprehensive market analysis.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        ETF ticker symbol (e.g., &quot;SPY&quot;, &quot;QQQ&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/etfs/SPY/quote', '/etfs/QQQ/quote']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">latestClose <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent closing price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">latestOpen <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent opening price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">latestHigh <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent high price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">latestLow <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent low price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">previousClose <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Previous closing price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">week52Range <span className="text-gray-500">object</span></p>
                  <p className="text-sm text-gray-600">52-week high/low range <code className="text-xs bg-gray-100 px-1 rounded">{'{ high: number, low: number }'}</code></p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">week52AvgClose <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Average closing price over the trailing 52 weeks</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ema30 <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">30-period exponential moving average</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ema60 <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">60-period exponential moving average</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ema90 <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">90-period exponential moving average</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ema120 <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">120-period exponential moving average</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ytdClosePctChange <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Year-to-date closing price percentage change</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">volume <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Most recent trading volume</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">avgVolumeLifetime <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Lifetime average trading volume</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">nav <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Net asset value per share</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">expense <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Expense ratio of the ETF</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ETF Quote" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">ETF Quote</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs/ticker-quote"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/quote"></Code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
